import { useState, useEffect, useContext, useCallback, useMemo, useRef, memo } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  List,
  ListItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  Autocomplete,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import AuthContext from '../../context/authContext';
import { useNews } from '../../hooks/useNews';
import { useGroups } from '../../hooks/useGroups';
import SimpleTextEditor from '../components/SimpleTextEditor';
import useHttp from '../../hooks/useHttp';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

const NewsList = ({ items, canUpdate, canDelete, onEdit, onDelete }) => {
  const formatDate = useCallback(dateString => dayjs(dateString).format('YYYY-MM-DD'), []);

  const getGroupNames = useCallback(newsItem => {
    if (!newsItem.groups || newsItem.groups.length === 0) {
      return 'Alle Gruppen';
    }
    return newsItem.groups.map(g => (typeof g === 'object' ? g.name : '')).join(', ');
  }, []);

  if (items.length === 0) {
    return <Alert severity='info'>Keine News vorhanden.</Alert>;
  }

  return (
    <List>
      {items.map(newsItem => (
        <Paper key={newsItem._id} sx={{ mb: 2 }}>
          <ListItem sx={{ flexDirection: 'column', alignItems: 'stretch' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 1,
                flexWrap: 'wrap',
                width: '100%',
              }}
            >
              <Typography variant='h6'>{newsItem.title}</Typography>
              <Chip label={formatDate(newsItem.date)} size='small' variant='outlined' />
              <Chip label={getGroupNames(newsItem)} size='small' variant='outlined' />
              {Array.isArray(newsItem.authors) &&
                newsItem.authors.map((a, idx) => {
                  const authorId = typeof a === 'object' ? a._id : a;
                  const name =
                    (typeof a === 'object' &&
                      ([a.firstName, a.lastName].filter(Boolean).join(' ') || a.username)) ||
                    a;
                  return (
                    <Chip
                      key={`${newsItem._id}-author-${authorId}-${idx}`}
                      label={name}
                      size='small'
                      variant='outlined'
                    />
                  );
                })}
              <Box
                sx={{
                  display: 'flex',
                  gap: 0.5,
                  ml: 'auto',
                  alignItems: 'center',
                  flexShrink: 0,
                }}
              >
                {canUpdate && (
                  <IconButton size='small' onClick={() => onEdit(newsItem)}>
                    <Edit fontSize='small' />
                  </IconButton>
                )}
                {canDelete && (
                  <IconButton
                    size='small'
                    onClick={() => onDelete(newsItem._id)}
                    sx={{ color: 'error.main' }}
                  >
                    <Delete fontSize='small' />
                  </IconButton>
                )}
              </Box>
            </Box>
            <Box sx={{ mt: 1, width: '100%' }} dangerouslySetInnerHTML={{ __html: newsItem.content }} />
          </ListItem>
        </Paper>
      ))}
    </List>
  );
};

const MemoNewsList = /*#__PURE__*/ memo(NewsList);

const NewsDialog = ({
  open,
  dialogKey,
  title,
  initialData,
  groups,
  users,
  onClose,
  onSubmit,
}) => {
  const titleInputRef = useRef(null);
  const contentRef = useRef(initialData?.content || '');
  const [dateValue, setDateValue] = useState(initialData?.date || dayjs());
  const [groupIds, setGroupIds] = useState(initialData?.groups || []);
  const [authorIds, setAuthorIds] = useState(initialData?.authors || []);

  useEffect(() => {
    contentRef.current = initialData?.content || '';
    setDateValue(initialData?.date || dayjs());
    setGroupIds(initialData?.groups || []);
    setAuthorIds(initialData?.authors || []);
  }, [initialData, open]);

  const groupNameById = useMemo(() => {
    const map = new Map();
    for (const g of groups) {
      map.set(g._id, g.name);
    }
    return map;
  }, [groups]);

  const userById = useMemo(() => {
    const map = new Map();
    for (const u of users) {
      map.set(u._id, u);
    }
    return map;
  }, [users]);

  const selectedAuthors = useMemo(() => {
    return authorIds.map(id => userById.get(id)).filter(Boolean);
  }, [authorIds, userById]);

  const handleDialogSubmit = useCallback(() => {
    const titleValue = titleInputRef.current?.value || '';
    const contentValue = contentRef.current || '';

    onSubmit({
      title: titleValue,
      content: contentValue,
      date: dateValue,
      groups: groupIds,
      authors: authorIds,
    });
  }, [authorIds, dateValue, groupIds, onSubmit]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box key={dialogKey} sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label='Titel'
            defaultValue={initialData?.title || ''}
            inputRef={titleInputRef}
            fullWidth
            required
          />
          <DateTimePicker
            label='Datum'
            value={dateValue}
            onChange={newValue => setDateValue(newValue)}
            slotProps={{ textField: { fullWidth: true } }}
          />
          <FormControl fullWidth>
            <InputLabel>Empfänger (leer = alle Gruppen)</InputLabel>
            <Select
              multiple
              label='Empfänger (leer = alle Gruppen)'
              value={groupIds}
              onChange={e => setGroupIds(e.target.value)}
              renderValue={selected => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map(value => (
                    <Chip key={value} label={groupNameById.get(value) || value} size='small' />
                  ))}
                </Box>
              )}
            >
              {groups.map(group => (
                <MenuItem key={group._id} value={group._id}>
                  {group.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box>
            <Typography variant='subtitle2' sx={{ mb: 1 }}>
              Autoren
            </Typography>
            <Autocomplete
              multiple
              options={users}
              getOptionLabel={u => [u.firstName, u.lastName].filter(Boolean).join(' ') || u.username || ''}
              isOptionEqualToValue={(option, value) => option?._id === value?._id}
              renderOption={(props, option) => (
                <li {...props} key={option._id}>
                  {[option.firstName, option.lastName].filter(Boolean).join(' ') || option.username || ''}
                </li>
              )}
              value={selectedAuthors}
              onChange={(_, newVal) => setAuthorIds(newVal.map(u => u._id))}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option._id}
                    label={[option.firstName, option.lastName].filter(Boolean).join(' ') || option.username}
                  />
                ))
              }
              renderInput={params => <TextField {...params} placeholder='Autoren auswählen' />}
            />
          </Box>
          <Box>
            <Typography variant='subtitle2' sx={{ mb: 1 }}>
              Inhalt
            </Typography>
            <SimpleTextEditor
              value={initialData?.content || ''}
              onChange={content => {
                contentRef.current = content;
              }}
              placeholder='News-Inhalt eingeben...'
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button onClick={handleDialogSubmit} variant='contained'>
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
};

function NewsManagement() {
  const authCtx = useContext(AuthContext);
  const { fetchNews, createNews, updateNews, deleteNews } = useNews();
  const { fetchGroups } = useGroups();
  const { sendRequest } = useHttp();
  const [news, setNews] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const didInitialLoadRef = useRef(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmNewsId, setConfirmNewsId] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [dialogInitialData, setDialogInitialData] = useState(null);
  const [dialogKey, setDialogKey] = useState('new');
  const [users, setUsers] = useState([]);

  if (!authCtx.checkRight('news:read')) {
    return <Navigate to='/welcome' replace />;
  }

  const canReadGroups = authCtx.checkRight('group:read');

  const loadData = useCallback(async ({ includeGroups }) => {
    setLoading(true);
    try {
      const newsPromise = fetchNews();
      let groupsPromise = Promise.resolve([]);
      if (includeGroups) {
        groupsPromise = fetchGroups().catch(() => []);
      }
      const [newsData, groupsData] = await Promise.all([newsPromise, groupsPromise]);
      setNews(newsData || []);
      setGroups(groupsData || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchGroups, fetchNews]);

  useEffect(() => {
    if (didInitialLoadRef.current) return;
    didInitialLoadRef.current = true;
    loadData({ includeGroups: canReadGroups });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsersForAuthors = useCallback(async () => {
    if (!authCtx.checkRight('user:read')) {
      setUsers([]);
      return;
    }
    try {
      const response = await sendRequest({
        url: '/api/v1/user',
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      });
      if (Array.isArray(response)) {
        setUsers(response);
      } else if (response && Array.isArray(response.users)) {
        setUsers(response.users);
      } else {
        setUsers([]);
      }
    } catch (_) {
      setUsers([]);
    }
  }, [authCtx, sendRequest]);

  const handleOpenDialog = useCallback(
    newsItem => {
      if (newsItem) {
        setEditingNews(newsItem);
        setDialogInitialData({
          title: newsItem.title || '',
          content: newsItem.content || '',
          date: newsItem.date ? dayjs(newsItem.date) : dayjs(),
          groups: newsItem.groups?.map(g => (typeof g === 'object' ? g._id : g)) || [],
          authors: newsItem.authors?.map(a => (typeof a === 'object' ? a._id : a)) || [],
        });
        setDialogKey(newsItem._id || 'edit');
      } else {
        setEditingNews(null);
        setDialogInitialData({
          title: '',
          content: '',
          date: dayjs(),
          groups: [],
          authors: authCtx.currentUser?.userId ? [authCtx.currentUser.userId] : [],
        });
        setDialogKey('new');
      }

      fetchUsersForAuthors();
      setOpenDialog(true);
    },
    [authCtx.currentUser?.userId, fetchUsersForAuthors]
  );

  const handleCloseDialog = useCallback(() => {
    setOpenDialog(false);
    setEditingNews(null);
    setDialogInitialData(null);
    setDialogKey('new');
  }, []);

  const handleSubmit = useCallback(
    async data => {
      if (!data?.title?.trim() || !data?.content?.trim()) {
        return;
      }

      try {
        const newsData = {
          title: data.title,
          content: data.content,
          date: dayjs(data.date).format('YYYY-MM-DD'),
          groups: data.groups,
          authors: data.authors,
        };

        if (editingNews) {
          await updateNews(editingNews._id, newsData);
        } else {
          await createNews(newsData);
        }

        handleCloseDialog();
        loadData({ includeGroups: canReadGroups });
      } catch (error) {
        console.error('Error saving news:', error);
      }
    },
    [canReadGroups, createNews, editingNews, handleCloseDialog, loadData, updateNews]
  );

  const openConfirmDelete = useCallback(newsId => {
    setConfirmNewsId(newsId);
    setConfirmOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!confirmNewsId) return;
    try {
      await deleteNews(confirmNewsId);
      setConfirmOpen(false);
      setConfirmNewsId(null);
      loadData({ includeGroups: canReadGroups });
    } catch (error) {
      console.error('Error deleting news:', error);
      setConfirmOpen(false);
      setConfirmNewsId(null);
    }
  }, [canReadGroups, confirmNewsId, deleteNews, loadData]);

  const handleCancelDelete = useCallback(() => {
    setConfirmOpen(false);
    setConfirmNewsId(null);
  }, []);

  const canCreate = authCtx.checkRight('news:create');
  const canUpdate = authCtx.checkRight('news:update');
  const canDelete = authCtx.checkRight('news:delete');

  const dialogTitle = editingNews ? 'News bearbeiten' : 'Neue News erstellen';

  const listItems = useMemo(() => news, [news]);

  if (loading) {
    return (
      <Box sx={{ ...viewRootTextPrimarySx, display: 'flex', justifyContent: 'center', padding: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='de'>
      <Box sx={{ ...viewRootTextPrimarySx, padding: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          {canCreate && (
            <Button variant='contained' startIcon={<Add />} onClick={() => handleOpenDialog(null)}>
              Newsartikel erstellen
            </Button>
          )}
        </Box>

        <MemoNewsList
          items={listItems}
          canUpdate={canUpdate}
          canDelete={canDelete}
          onEdit={handleOpenDialog}
          onDelete={openConfirmDelete}
        />

        <Dialog open={confirmOpen} onClose={handleCancelDelete}>
          <DialogTitle>News löschen</DialogTitle>
          <DialogContent>Möchten Sie diese News wirklich löschen?</DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete}>Abbrechen</Button>
            <Button
              color='error'
              variant='contained'
              onClick={handleConfirmDelete}
              sx={{ backgroundColor: 'error.main', '&:hover': { backgroundColor: 'error.dark' } }}
            >
              Löschen
            </Button>
          </DialogActions>
        </Dialog>

        <NewsDialog
          open={openDialog}
          dialogKey={dialogKey}
          title={dialogTitle}
          initialData={dialogInitialData}
          groups={groups}
          users={users}
          onClose={handleCloseDialog}
          onSubmit={handleSubmit}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default NewsManagement;

